"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import CarrierPlanSelect, { type CarrierPlanSelection, formatPlanClaimsAddress } from "@/components/insurance/carrier-plan-select";
import { Checkbox, DateOfBirthInput, Input, Label } from "@/components/ui";
import PhoneNumberInput from "@/components/ui/phone-number-input";
import { COVERAGE_TYPE_LABELS, PLAN_TYPE_LABELS } from "@/types/insurance-carrier";
import type { InsuranceCoverage, InsuranceCoverageCreate, InsuranceCoverageUpdate } from "@/types/insurance-coverage";

/**
 * The one coverage form.
 *
 * A coverage can be entered from the patient chart or from inside the Super Bill
 * wizard. Those were two hand-written forms over the same table, and they drifted:
 * the wizard's had no carrier picker and no claims-address field at all, so a
 * coverage added while building a bill could never carry the address that bill
 * prints. Same record, same endpoint, one form.
 *
 * Deliberately presentational. It owns no draft state, no mutation and no dialog
 * chrome, because the two hosts genuinely differ there — the chart saves per card
 * and can collapse, the wizard saves through the step it is embedded in. What they
 * must not differ on is which fields a coverage has.
 */

export interface CoverageDraft {
  insurerName: string;
  memberId: string;
  policyHolderName: string;
  policyHolderDob: string;
  isPrimary: boolean;
  inNetwork: boolean;
  groupId: string;
  phoneNumber: string;
  planId: string | null;
  pverifyCode: string | null;
  coverageType: string;
  planName: string;
  planType: string;
  effectiveDate: string;
  effectiveTo: string;
  eligibilityNote: string;
  claimsAddress: string;
}
export const emptyCoverageDraft: CoverageDraft = {
  insurerName: "",
  memberId: "",
  policyHolderName: "",
  policyHolderDob: "",
  // Non-primary by default so adding a coverage doesn't quietly demote the
  // patient's existing primary.
  isPrimary: false,
  inNetwork: true,
  groupId: "",
  phoneNumber: "",
  planId: null,
  pverifyCode: null,
  coverageType: "",
  planName: "",
  planType: "",
  effectiveDate: "",
  effectiveTo: "",
  eligibilityNote: "",
  claimsAddress: ""
};
export function coverageToDraft(coverage: InsuranceCoverage): CoverageDraft {
  return {
    insurerName: coverage.insurerName,
    memberId: coverage.memberId,
    policyHolderName: coverage.policyHolderName ?? "",
    policyHolderDob: coverage.policyHolderDob ?? "",
    isPrimary: coverage.isPrimary,
    inNetwork: coverage.inNetwork,
    groupId: coverage.groupId ?? "",
    phoneNumber: coverage.phoneNumber ?? "",
    planId: coverage.planId ?? null,
    pverifyCode: coverage.pverifyCode ?? null,
    coverageType: coverage.coverageType ?? "",
    planName: coverage.planName ?? "",
    planType: coverage.planType ?? "",
    effectiveDate: coverage.effectiveDate ?? "",
    effectiveTo: coverage.effectiveTo ?? "",
    eligibilityNote: coverage.eligibilityNote ?? "",
    claimsAddress: coverage.claimsAddress ?? ""
  };
}

/**
 * Apply an Insurer selection to the draft.
 *
 * Picking from the catalog snapshots the plan's values onto the coverage — they stay
 * editable afterwards, and the snapshot is what reaches the chart and the printed bill.
 * Keeping a typed value instead only sets the name and drops any previous plan link,
 * leaving the other fields for the user to fill in by hand.
 */
export function applyCarrierPlan(draft: CoverageDraft, selection: CarrierPlanSelection): CoverageDraft {
  const {
    carrier,
    plan,
    insurerName
  } = selection;
  if (!carrier) {
    // Only when leaving a catalog plan. Those fields were filled from it, and keeping
    // them would run eligibility against the carrier just replaced and post the claim
    // to its address. A coverage that was always free text keeps what was typed.
    if (!draft.planId) return {
      ...draft,
      insurerName
    };
    return {
      ...draft,
      insurerName,
      planId: null,
      pverifyCode: null,
      planName: "",
      planType: "",
      coverageType: "",
      claimsAddress: "",
      phoneNumber: ""
    };
  }
  return {
    ...draft,
    insurerName,
    planId: plan?.id ?? null,
    pverifyCode: carrier.code ?? null,
    inNetwork: carrier.isInNetwork,
    planName: plan?.name ?? "",
    // The label, not the enum token: insurance_coverage.plan_type is free text and
    // it is what prints on the Super Bill, so "AUTO_PIP" would reach a carrier.
    planType: plan?.planType ? PLAN_TYPE_LABELS[plan.planType] : "",
    coverageType: plan?.coverageType ? COVERAGE_TYPE_LABELS[plan.coverageType] : "",
    claimsAddress: plan ? formatPlanClaimsAddress(plan) : "",
    phoneNumber: plan?.claimsPhone?.trim() || ""
  };
}
export function coverageCreatePayload(draft: CoverageDraft, patientId: string): InsuranceCoverageCreate {
  return {
    patientId,
    insurerName: draft.insurerName.trim(),
    memberId: draft.memberId.trim(),
    policyHolderName: draft.policyHolderName.trim() || null,
    policyHolderDob: draft.policyHolderDob || null,
    isPrimary: draft.isPrimary,
    inNetwork: draft.inNetwork,
    groupId: draft.groupId.trim() || null,
    phoneNumber: draft.phoneNumber.trim() || null,
    planId: draft.planId,
    pverifyCode: draft.pverifyCode,
    coverageType: draft.coverageType.trim() || null,
    planName: draft.planName.trim() || null,
    planType: draft.planType.trim() || null,
    effectiveDate: draft.effectiveDate || null,
    effectiveTo: draft.effectiveTo || null,
    eligibilityNote: draft.eligibilityNote.trim() || null,
    claimsAddress: draft.claimsAddress.trim() || null
  };
}
export function coverageUpdatePayload(draft: CoverageDraft): InsuranceCoverageUpdate {
  const {
    patientId: _patientId,
    ...rest
  } = coverageCreatePayload(draft, "");
  return rest;
}

/** The required-field rule, stated once. Returns the reason, so a host that wants
 *  to print it can, and a host that only wants to disable Save can test for null. */
export function coverageDraftError(draft: CoverageDraft): string | null {
  if (!draft.insurerName.trim()) return "Insurer is required.";
  if (!draft.memberId.trim()) return "Member ID is required.";
  return null;
}
interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}
function Field(t0) {
  const $ = _c(6);
  const {
    label,
    required,
    children
  } = t0;
  let t1;
  if ($[0] !== label || $[1] !== required) {
    t1 = <Label mandatory={required}>{label}</Label>;
    $[0] = label;
    $[1] = required;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <div className="space-y-1">{t1}{children}</div>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
interface CoverageFieldsProps {
  value: CoverageDraft;
  onChange: (next: CoverageDraft) => void;
  disabled?: boolean;
  /** DateOfBirthInput only writes back a fully-typed valid date, so a partial edit
   *  leaves the draft on the old value. Hosts use this to block Save mid-typing. */
  onDobValidityChange?: (isValid: boolean) => void;
}
export function CoverageFields(t0) {
  const $ = _c(116);
  const {
    value,
    onChange,
    disabled,
    onDobValidityChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [touched, setTouched] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = key => () => setTouched(t => t[key] ? t : {
      ...t,
      [key]: true
    });
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const blurred = t2;
  let t3;
  if ($[2] !== touched) {
    t3 = (key_0, isEmpty) => !!touched[key_0] && isEmpty;
    $[2] = touched;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const errored = t3;
  let t4;
  if ($[4] !== onChange || $[5] !== value) {
    t4 = (key_1, next) => onChange({
      ...value,
      [key_1]: next
    });
    $[4] = onChange;
    $[5] = value;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const set = t4;
  const t5 = value.insurerName;
  const t6 = value.planId;
  let t7;
  if ($[7] !== onChange || $[8] !== value) {
    t7 = selection => onChange(applyCarrierPlan(value, selection));
    $[7] = onChange;
    $[8] = value;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== value.insurerName) {
    t8 = value.insurerName.trim();
    $[10] = value.insurerName;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = !t8;
  let t10;
  if ($[12] !== errored || $[13] !== t9) {
    t10 = errored("insurerName", t9);
    $[12] = errored;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== disabled || $[16] !== t10 || $[17] !== t7 || $[18] !== value.insurerName || $[19] !== value.planId) {
    t11 = <div className="md:col-span-2"><Field label="Insurer" required={true}><CarrierPlanSelect value={t5} planId={t6} onSelect={t7} disabled={disabled} hasError={t10} dataTestId="coverage-carrier-select" /></Field></div>;
    $[15] = disabled;
    $[16] = t10;
    $[17] = t7;
    $[18] = value.insurerName;
    $[19] = value.planId;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== set) {
    t12 = e => set("planName", e.target.value);
    $[21] = set;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== disabled || $[24] !== t12 || $[25] !== value.planName) {
    t13 = <Field label="Plan name"><Input value={value.planName} onChange={t12} disabled={disabled} /></Field>;
    $[23] = disabled;
    $[24] = t12;
    $[25] = value.planName;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== set) {
    t14 = e_0 => set("planType", e_0.target.value);
    $[27] = set;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== disabled || $[30] !== t14 || $[31] !== value.planType) {
    t15 = <Field label="Plan type"><Input value={value.planType} onChange={t14} placeholder="e.g., PPO, HMO" disabled={disabled} /></Field>;
    $[29] = disabled;
    $[30] = t14;
    $[31] = value.planType;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== set) {
    t16 = e_1 => set("coverageType", e_1.target.value);
    $[33] = set;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== disabled || $[36] !== t16 || $[37] !== value.coverageType) {
    t17 = <Field label="Coverage type"><Input value={value.coverageType} onChange={t16} placeholder="e.g., Medical, Vision" disabled={disabled} /></Field>;
    $[35] = disabled;
    $[36] = t16;
    $[37] = value.coverageType;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== set) {
    t18 = e_2 => set("claimsAddress", e_2.target.value);
    $[39] = set;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== disabled || $[42] !== t18 || $[43] !== value.claimsAddress) {
    t19 = <Field label="Claims address"><Input value={value.claimsAddress} onChange={t18} disabled={disabled} /></Field>;
    $[41] = disabled;
    $[42] = t18;
    $[43] = value.claimsAddress;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== set) {
    t20 = e_3 => set("phoneNumber", e_3.target.value);
    $[45] = set;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== disabled || $[48] !== t20 || $[49] !== value.phoneNumber) {
    t21 = <Field label="Claims phone"><PhoneNumberInput value={value.phoneNumber} onChange={t20} disabled={disabled} /></Field>;
    $[47] = disabled;
    $[48] = t20;
    $[49] = value.phoneNumber;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  const t22 = value.memberId;
  let t23;
  if ($[51] !== set) {
    t23 = e_4 => set("memberId", e_4.target.value);
    $[51] = set;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = blurred("memberId");
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== value.memberId) {
    t25 = value.memberId.trim();
    $[54] = value.memberId;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  const t26 = !t25;
  let t27;
  if ($[56] !== errored || $[57] !== t26) {
    t27 = errored("memberId", t26);
    $[56] = errored;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== disabled || $[60] !== t23 || $[61] !== t27 || $[62] !== value.memberId) {
    t28 = <Field label="Member ID" required={true}><Input value={t22} onChange={t23} onBlur={t24} placeholder="Member ID" hasError={t27} disabled={disabled} /></Field>;
    $[59] = disabled;
    $[60] = t23;
    $[61] = t27;
    $[62] = value.memberId;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== set) {
    t29 = e_5 => set("groupId", e_5.target.value);
    $[64] = set;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== disabled || $[67] !== t29 || $[68] !== value.groupId) {
    t30 = <Field label="Group ID"><Input value={value.groupId} onChange={t29} disabled={disabled} /></Field>;
    $[66] = disabled;
    $[67] = t29;
    $[68] = value.groupId;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== set) {
    t31 = e_6 => set("policyHolderName", e_6.target.value);
    $[70] = set;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== disabled || $[73] !== t31 || $[74] !== value.policyHolderName) {
    t32 = <Field label="Policy holder"><Input value={value.policyHolderName} onChange={t31} placeholder="Name on the policy" disabled={disabled} /></Field>;
    $[72] = disabled;
    $[73] = t31;
    $[74] = value.policyHolderName;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== set) {
    t33 = next_0 => set("policyHolderDob", next_0 ?? "");
    $[76] = set;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = new Date();
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== disabled || $[80] !== onDobValidityChange || $[81] !== t33 || $[82] !== value.policyHolderDob) {
    t35 = <Field label="Policy holder DOB"><DateOfBirthInput value={value.policyHolderDob} onChange={t33} onValidityChange={onDobValidityChange} maxDate={t34} className="w-full" disabled={disabled} /></Field>;
    $[79] = disabled;
    $[80] = onDobValidityChange;
    $[81] = t33;
    $[82] = value.policyHolderDob;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== set) {
    t36 = e_7 => set("effectiveDate", e_7.target.value);
    $[84] = set;
    $[85] = t36;
  } else {
    t36 = $[85];
  }
  let t37;
  if ($[86] !== disabled || $[87] !== t36 || $[88] !== value.effectiveDate) {
    t37 = <Field label="Effective date"><Input type="date" value={value.effectiveDate} onChange={t36} disabled={disabled} /></Field>;
    $[86] = disabled;
    $[87] = t36;
    $[88] = value.effectiveDate;
    $[89] = t37;
  } else {
    t37 = $[89];
  }
  let t38;
  if ($[90] !== set) {
    t38 = e_8 => set("effectiveTo", e_8.target.value);
    $[90] = set;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  let t39;
  if ($[92] !== disabled || $[93] !== t38 || $[94] !== value.effectiveTo) {
    t39 = <Field label="Effective through"><Input type="date" value={value.effectiveTo} onChange={t38} disabled={disabled} /></Field>;
    $[92] = disabled;
    $[93] = t38;
    $[94] = value.effectiveTo;
    $[95] = t39;
  } else {
    t39 = $[95];
  }
  let t40;
  if ($[96] !== set) {
    t40 = e_9 => set("eligibilityNote", e_9.target.value);
    $[96] = set;
    $[97] = t40;
  } else {
    t40 = $[97];
  }
  let t41;
  if ($[98] !== disabled || $[99] !== t40 || $[100] !== value.eligibilityNote) {
    t41 = <div className="md:col-span-2"><Field label="Eligibility notes"><Input value={value.eligibilityNote} onChange={t40} disabled={disabled} /></Field></div>;
    $[98] = disabled;
    $[99] = t40;
    $[100] = value.eligibilityNote;
    $[101] = t41;
  } else {
    t41 = $[101];
  }
  let t42;
  if ($[102] !== t11 || $[103] !== t13 || $[104] !== t15 || $[105] !== t17 || $[106] !== t19 || $[107] !== t21 || $[108] !== t28 || $[109] !== t30 || $[110] !== t32 || $[111] !== t35 || $[112] !== t37 || $[113] !== t39 || $[114] !== t41) {
    t42 = <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{t11}{t13}{t15}{t17}{t19}{t21}{t28}{t30}{t32}{t35}{t37}{t39}{t41}</div>;
    $[102] = t11;
    $[103] = t13;
    $[104] = t15;
    $[105] = t17;
    $[106] = t19;
    $[107] = t21;
    $[108] = t28;
    $[109] = t30;
    $[110] = t32;
    $[111] = t35;
    $[112] = t37;
    $[113] = t39;
    $[114] = t41;
    $[115] = t42;
  } else {
    t42 = $[115];
  }
  return t42;
}
interface CoverageStatusTogglesProps {
  value: CoverageDraft;
  onChange: (next: CoverageDraft) => void;
  disabled?: boolean;
  /** The chart syncs `isPrimary` from the server until the user touches it. */
  onPrimaryToggled?: () => void;
}

/**
 * Primary / in-network, as a separate export rather than a `showToggles` prop on
 * CoverageFields: the wizard decides the primary payer by selection order in its
 * own step, so a second control saying the same thing there would contradict it.
 */
export function CoverageStatusToggles(t0) {
  const $ = _c(24);
  const {
    value,
    onChange,
    disabled,
    onPrimaryToggled
  } = t0;
  let t1;
  if ($[0] !== onChange || $[1] !== onPrimaryToggled || $[2] !== value) {
    t1 = checked => {
      onPrimaryToggled?.();
      onChange({
        ...value,
        isPrimary: checked === true
      });
    };
    $[0] = onChange;
    $[1] = onPrimaryToggled;
    $[2] = value;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== value.isPrimary) {
    t2 = <Checkbox checked={value.isPrimary} disabled={disabled} onCheckedChange={t1} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = value.isPrimary;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-body text-foreground">Primary coverage</span>;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2) {
    t4 = <label className="flex cursor-pointer items-center gap-2">{t2}{t3}</label>;
    $[9] = t2;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== onChange || $[12] !== value) {
    t5 = checked_0 => onChange({
      ...value,
      inNetwork: checked_0 === true
    });
    $[11] = onChange;
    $[12] = value;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== disabled || $[15] !== t5 || $[16] !== value.inNetwork) {
    t6 = <Checkbox checked={value.inNetwork} disabled={disabled} onCheckedChange={t5} />;
    $[14] = disabled;
    $[15] = t5;
    $[16] = value.inNetwork;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="text-body text-foreground">In-network</span>;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t6) {
    t8 = <label className="flex cursor-pointer items-center gap-2">{t6}{t7}</label>;
    $[19] = t6;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t4 || $[22] !== t8) {
    t9 = <div className="flex flex-wrap items-center gap-x-6 gap-y-2">{t4}{t8}</div>;
    $[21] = t4;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}